import { zodResolver } from '@hookform/resolvers/zod'
import { useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useParams } from 'react-router-dom'

import { UiButton } from '@/ui/UiButton'

import ProposalPreview from '../components/ProposalPreview'
import ProposalJsonEditor from './components/ProposalJsonEditor'
import UploadZone from './components/UploadZone'
import { useSubmitMultisigProposal } from './hooks/submit-multisig-proposal'
import { CreateProposalFormValues, createProposalSchema } from './schema'
import { parseProposalJsonMessages } from './utils/parse-proposal-json'

export default function CreateProposalManual() {
  const { account } = useParams<{ account: string }>()

  const form = useForm<CreateProposalFormValues>({
    resolver: zodResolver(createProposalSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    defaultValues: {
      rawJsonText: '',
    },
  })

  const submitProposalTx = useSubmitMultisigProposal({ group: account ?? '' })

  const rawJsonText = useWatch({
    control: form.control,
    name: 'rawJsonText',
  })

  const parsedMessages = useMemo(() => {
    if (!form.formState.isValid) return null

    try {
      return parseProposalJsonMessages(rawJsonText ?? '')
    } catch {
      return null
    }
  }, [form.formState.isValid, rawJsonText])

  const isSubmitting = submitProposalTx.isPending
  const isSubmitDisabled = !form.formState.isValid || isSubmitting

  return (
    <div className='space-y-4'>
      <UploadZone
        isDisabled={isSubmitting}
        setRawJsonText={nextText => {
          form.setValue('rawJsonText', nextText, {
            shouldDirty: true,
            shouldValidate: true,
          })
        }}
        triggerValidation={() => form.trigger('rawJsonText')}
      />
      <ProposalJsonEditor
        rawJsonTextField={form.register('rawJsonText')}
        errorMessage={form.formState.errors.rawJsonText?.message}
        isDisabled={isSubmitting}
      />

      {parsedMessages && <ProposalPreview messages={parsedMessages} />}

      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end'>
        <UiButton
          type='button'
          disabled={isSubmitDisabled}
          onClick={form.handleSubmit(values => {
            submitProposalTx.mutate(values)
          })}
        >
          {isSubmitting ? 'Submitting…' : 'Submit proposal'}
        </UiButton>
      </div>
    </div>
  )
}
